import React, {useEffect, useState} from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import {Add, Delete, Edit} from "@mui/icons-material";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {GET_ADMIN_ON_CHANGE_HANDLER_SETS} from "../../components/graphql/queries/gqlAdminSettings";
import {DELETE_ADMIN_ON_CHANGE_HANDLER_SET, SAVE_ADMIN_ON_CHANGE_HANDLER_SET} from "../../components/graphql/mutations/gqlAdminMutations";
import {ON_CHANGE_HANDLER_TYPE_MAP, ON_CHANGE_HANDLER_TYPE_OPTIONS, OnChangeHandlerSet} from "../adminTypes";
import {SettingRowEditor, SettingRowData} from "./SettingRowEditor";

export function OnChangeHandlerSetsTab() {
	const [sets, setSets] = useState<OnChangeHandlerSet[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [editingSet, setEditingSet] = useState<OnChangeHandlerSet | null>(null);
	const [saving, setSaving] = useState(false);

	const loadSets = async () => {
		setLoading(true);
		setError(null);
		try {
			const result = await graphQLServices.documentsClient.query({
				query: GET_ADMIN_ON_CHANGE_HANDLER_SETS,
				fetchPolicy: "no-cache",
			});
			setSets((result.data.adminOnChangeHandlerSets ?? []).map((s: any) => ({
				...s,
				onChangeHandlers: (s.onChangeHandlers ?? []).map((h: any) => ({
					...h,
					onChangeHandlerType: typeof h.onChangeHandlerType === "string"
						? (ON_CHANGE_HANDLER_TYPE_MAP[h.onChangeHandlerType] ?? h.onChangeHandlerType)
						: h.onChangeHandlerType,
				})),
			})));
		} catch (e: any) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadSets(); 
	}, []);

	const handleEdit = (set: OnChangeHandlerSet) => setEditingSet(JSON.parse(JSON.stringify(set)));
	const handleNew = () => setEditingSet({id: 0, code: "", description: "", onChangeHandlers: []});

	const handleDelete = async (id: number) => {
		if (!window.confirm("Delete this on-change handler set?")) return;
		try {
			await graphQLServices.documentsClient.mutate({
				mutation: DELETE_ADMIN_ON_CHANGE_HANDLER_SET,
				variables: {input: {id}},
			});
			await loadSets();
		} catch (e: any) {
			setError(e.message);
		}
	};

	const handleSave = async () => {
		if (!editingSet) return;
		setSaving(true);
		setError(null);
		try {
			await graphQLServices.documentsClient.mutate({
				mutation: SAVE_ADMIN_ON_CHANGE_HANDLER_SET,
				variables: {
					input: {
						id: editingSet.id || undefined,
						code: editingSet.code,
						description: editingSet.description,
						onChangeHandlers: editingSet.onChangeHandlers.map(h => ({
							id: h.id || undefined,
							order: h.order,
							onChangeHandlerType: h.onChangeHandlerType,
							settings: h.settings,
							description: h.description,
						})),
					},
				},
			});
			setEditingSet(null);
			await loadSets();
		} catch (e: any) {
			setError(e.message);
		} finally {
			setSaving(false);
		}
	};

	const updateRow = (idx: number, updated: SettingRowData) => {
		if (!editingSet) return;
		const handlers = [...editingSet.onChangeHandlers];
		handlers[idx] = {
			...handlers[idx],
			order: updated.order,
			onChangeHandlerType: updated.type,
			settings: updated.settings,
			description: updated.description,
		};
		setEditingSet({...editingSet, onChangeHandlers: handlers});
	};

	if (loading) return <CircularProgress />;

	return (
		<Box sx={{mt: 2}}>
			{error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
			<Button startIcon={<Add />} variant="contained" onClick={handleNew} sx={{mb: 2}}>
				Add OnChange Handler Set
			</Button>
			{sets.map(set => (
				<Card key={set.id} sx={{mb: 2}}>
					<CardContent>
						<Typography variant="h6">{set.code}</Typography>
						<Typography variant="body2" color="text.secondary">{set.description}</Typography>
						<Typography variant="caption">{set.onChangeHandlers.length} handler(s)</Typography>
					</CardContent>
					<CardActions>
						<IconButton onClick={() => handleEdit(set)}><Edit /></IconButton>
						<IconButton color="error" onClick={() => handleDelete(set.id)}><Delete /></IconButton>
					</CardActions>
				</Card>
			))}

			<Dialog open={!!editingSet} onClose={() => setEditingSet(null)} maxWidth="lg" fullWidth>
				<DialogTitle>{editingSet?.id ? "Edit OnChange Handler Set" : "New OnChange Handler Set"}</DialogTitle>
				<DialogContent>
					{editingSet && (
						<Box sx={{mt: 1}}>
							{error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
							<TextField fullWidth label="Code" value={editingSet.code}
								onChange={e => setEditingSet({...editingSet, code: e.target.value})}
								sx={{mb: 2}} />
							<TextField fullWidth label="Description" value={editingSet.description}
								onChange={e => setEditingSet({...editingSet, description: e.target.value})}
								sx={{mb: 2}} />
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Order</TableCell>
										<TableCell>Type</TableCell>
										<TableCell>Description</TableCell>
										<TableCell>Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{[...editingSet.onChangeHandlers]
										.map((h, originalIdx) => ({h, originalIdx}))
										.sort((a, b) => a.h.order - b.h.order)
										.map(({h, originalIdx}) => (
											<SettingRowEditor
												key={h.id || `new-${originalIdx}`}
												setting={{
													id: h.id,
													order: h.order,
													type: h.onChangeHandlerType,
													settings: h.settings,
													description: h.description,
												}}
												typeOptions={ON_CHANGE_HANDLER_TYPE_OPTIONS}
												onChange={updated => updateRow(originalIdx, updated)}
												onDelete={() => setEditingSet({
													...editingSet,
													onChangeHandlers: editingSet.onChangeHandlers.filter((_, i) => i !== originalIdx),
												})}
											/>
										))}
								</TableBody>
							</Table>
							<Button startIcon={<Add />} sx={{mt: 1}} onClick={() =>
								setEditingSet({
									...editingSet,
									onChangeHandlers: [...editingSet.onChangeHandlers, {
										id: 0, order: editingSet.onChangeHandlers.length + 1,
										onChangeHandlerType: 0, settings: "{}", description: null,
									}],
								})
							}>
								Add Handler
							</Button>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEditingSet(null)}>Cancel</Button>
					<Button variant="contained" onClick={handleSave} disabled={saving}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}